<template>
  <div
    class="bg-white rounded-md shadow p-3 flex flex-col gap-6"
  >
    <h2 class="text-xl">
      Statistiques
    </h2>
    <template v-if="!loading && data">
      <div
        v-if="!pharmaMLsStatisticsEmpty"
        class="flex flex-row gap-6 justify-between"
      >
        <pharma-ml-statistics-card
          :loading="loading"
          :data="data.pharma_ml.last_seven_day_statistics"
          label="Sur les 7 derniers jours"
          class="flex-1"
        />
        <pharma-ml-statistics-card
          :data="data.pharma_ml.last_thirty_day_statistics"
          label="Sur les 30 derniers jours"
          class="flex-1"
        />
      </div>
      <div class="flex flex-row gap-24 flex-wrap justify-between items-center">
        <stock-statistic-card
          :value="data.stock.average_stock_quantity.toFixed(0)+'u'"
          label="Stock moyen dans les pharmacies"
          class="flex-1"
        />
        <stock-statistic-card
          :value="data.stock.average_stock_available_days.toFixed(1)"
          label="Stock journalier moyen dans les pharmacies"
          class="flex-1"
        />
        <stock-statistic-card
          :value="data.stock.max_stock_quantity"
          label="Stock maximum dans les pharmacies"
          class="flex-1"
        />
      </div>
      <wholesalers-availability-line-chart
        v-if="!wholesalerAvailabilityEmpty"
        :data="data.pharma_ml.rate_histogram"
      />
      <div
        v-if="rateByWholesalerEmpty && wholesalerAvailabilityEmpty && pharmaMLsStatisticsEmpty"
        class="text-center flex flex-col items-center"
      >
        <dot-lottie-vue
          src="animations/empty-transactions.json"
          style="height: 300px;"
          :loop="false"
          :autoplay="true"
        />
        <p>Aucune donnée grossiste disponible.</p>
      </div>
    </template>
    <base-flex-spinner
      v-else
      class="flex justify-center items-center"
    />
  </div>
</template>

<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import {disruptionLogisticsApi} from "@/container";
import {ProductAvailabilityStatistics} from "@/disruption-logistics/interfaces/availability-providers";
import StockStatisticCard from "@/disruption-logistics/components/availability-statistics/StockStatisticCard.vue";
import WholesalersAvailabilityLineChart
    from "@/disruption-logistics/components/availability-statistics/WholesalersAvailabilityLineChart.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import PharmaMlStatisticsCard
    from "@/disruption-logistics/components/availability-statistics/PharmaMlStatisticsCard.vue";
import {DotLottieVue} from "@lottiefiles/dotlottie-vue";

const props = defineProps<{productId: number, requestToken: string}>();
const emits = defineEmits(['pharmaMLUpdated']);

const data = ref<ProductAvailabilityStatistics|null>(null);
const loading = ref(false);

/**
 * Determine if the pharma ML statistics are empty.
 */
const pharmaMLsStatisticsEmpty = computed((): boolean => {
    if (loading.value || !data.value) {
        return false;
    }

    return !data.value.pharma_ml.last_seven_day_statistics.doc_count &&
        !data.value.pharma_ml.last_thirty_day_statistics.doc_count;
});

/**
 * Determine if the wholesalers availability is empty.
 *
 * @return {boolean}
 */
const wholesalerAvailabilityEmpty = computed((): boolean => {
    if (loading.value || !data.value) {
        return false;
    }

    return data.value.pharma_ml.rate_histogram.every(item => !item.rate);
});

/**
 * Determine if the rate by wholesaler is empty.
 */
const rateByWholesalerEmpty = computed((): boolean => {
    if (loading.value || !data.value) {
        return false;
    }

    return data.value.pharma_ml.wholesalers_histogram.length === 0;
});

/**
 * Load the availability statistics.
 */
const loadStatistics = async (productId: number): Promise<ProductAvailabilityStatistics> => {
    return disruptionLogisticsApi()
        .productAvailabilityStatistics(productId, props.requestToken);
};

watch(() => props.productId, async (value) => {
    loading.value = true;
    try {
        data.value = await loadStatistics(value);
        emits('pharmaMLUpdated', data.value.pharma_ml);
    } finally {
        loading.value = false;
    }
}, {immediate: true});

</script>

<style scoped>

</style>
